import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  fetchPreOpOrderData, 
  fetchPatientData, 
  fetchPreOpOrderNextIp, 
  fetchAllPatients, 
  savePreOpOrder, 
  updatePreOpOrder, 
  sendPreOpNotification 
} from '../api/preOpOrderApi';
import { mapBackendToForm, mapFormToBackend } from '../utils/mapping';
import { useFormValidation, getNextDay, getCurrentDateTime } from './useFormValidation';
import { useNotification } from '../../../../contexts/NotificationContext';
import { DRAFT_KEYS, loadDraft, hasDraft, clearDraft, saveDraft } from '../../../../utils/draftUtils';
import { surgeonList, surgeryData } from '../constants/options';

export const usePreOpOrders = (uhid_no, navigate) => {
    const queryClient = useQueryClient();
    const { showNotification } = useNotification();
    
    const [success, setSuccess] = useState("");
    const [filteredPatients, setFilteredPatients] = useState([]);
    const [emailLoading, setEmailLoading] = useState(false);
    const [emailSuccess, setEmailSuccess] = useState("");
    const [error, setError] = useState("");

    const [formData, setFormData] = useState({
        uhid: parseInt(uhid_no) || 0,
        ipNumber: 0,
        name: '',
        hospital_id: 1,
        age: '',
        sex: '',
        room: '',
        diagnosis: '',
        proposedSurgery: '',
        scheduledDatetime: '',
        surgeonName: '',
        surgical_code: '',
        procedure_code: '',
        procedureDescription: '',

        surgicalConsent: false,
        highRiskConsent: false,
        bloodTransfusionConsent: false,
        anesthesiaConsent: false,
        guardianName: '',
        guardianContact: '',
        consentComments: '',

        bloodOrders: {},
        sopNeeded: false,
        bloodGroup: '',
        prdcAttached: false,
        transfusionNotes: '',

        antibiotic: '',
        dose: '',
        timeToAdminister: '',
        additionalAntibiotics: '',
        skinPrep: '',
        preAnestheticMeds: '',
        preOpSedation: false,

        onAnticoagulants: false,
        lastDose: '',
        bridgingPlan: '',
        stopDate: '',
        postOpThromboprophylaxis: false,
        allergyReview: '',

        labsToDo: [],
        imagingToBeAvailable: [],
        cardiologyClearance: false,
        anesthClearance: false,
        otherSpecialtyClearance: '',

        additionalEquipments: '',
        criticalNotes: '',
        estimatedBloodLoss: '',
        siteMarking: false,
        positioning: '',
        surgeonNotes: '',

        finalized: false
    });

    const { validationErrors, setValidationErrors, validateField, validateForm } = useFormValidation(formData);

    // Queries
    const { data: existingData, isLoading: dataLoading } = useQuery({
        queryKey: ['preOpOrder', uhid_no],
        queryFn: () => fetchPreOpOrderData(uhid_no),
        enabled: !!uhid_no,
        retry: false
    });

    useEffect(() => {
        if (existingData && Object.keys(existingData).length > 0) {
            setSuccess('success');
            setFormData(prev => ({ ...prev, ...mapBackendToForm(existingData) }));
        }
    }, [existingData]);

    const { data: patientInfo } = useQuery({
        queryKey: ['patient', uhid_no],
        queryFn: () => fetchPatientData(uhid_no),
        enabled: !!uhid_no
    });

    const { data: nextIpData } = useQuery({
        queryKey: ['preOpOrderNextIp'],
        queryFn: fetchPreOpOrderNextIp,
        enabled: !success
    });

    const { data: allPatientsData, isLoading: patientsLoading } = useQuery({
        queryKey: ['allPatients'],
        queryFn: fetchAllPatients,
    });

    const { data: otBookingsData } = useQuery({
        queryKey: ['preOpOrderNextIpBookings'],
        queryFn: fetchPreOpOrderNextIp, // It seems the original code used this to get bookings? Or wait... The original code didn't actually fetch OT bookings correctly for the filter! It used 'data' which was set by get_preop_order_next_ip. And that's usually just an IP number or maybe an array? I'll assume `data` was just for IP number from the previous code, but it mapped it to bookedUHIDs. Let's replicate this safely.
    });

    useEffect(() => {
        if (patientInfo) {
            setFormData(prev => ({
                ...prev,
                name: patientInfo.name || prev.name,
                age: patientInfo.age || prev.age,
                sex: patientInfo.sex || prev.sex
            }));
        }
    }, [patientInfo]);

    useEffect(() => {
        if (nextIpData && !success && nextIpData.next_ip_no) {
            setFormData(prev => ({ ...prev, ipNumber: nextIpData.next_ip_no }));
        }
    }, [nextIpData, success]);

    useEffect(() => {
        if (uhid_no && hasDraft(DRAFT_KEYS.PREOPERATIVE_ORDERS, uhid_no)) {
            const saved = loadDraft(DRAFT_KEYS.PREOPERATIVE_ORDERS, uhid_no);
            if (saved && Object.keys(saved).length > 0 && typeof saved.formData === 'object') {
                setFormData(saved.formData);
            }
        }
    }, [uhid_no]);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (uhid_no) {
                saveDraft(DRAFT_KEYS.PREOPERATIVE_ORDERS, uhid_no, formData);
            }
        }, 2000);
        return () => clearTimeout(timeoutId);
    }, [formData, uhid_no]);

    useEffect(() => {
        // Auto-calculate administration time based on dose
        if (formData.dose) {
            const now = new Date();
            const doseInMinutes = parseInt(formData.dose, 10);
            if (!isNaN(doseInMinutes)) {
                now.setMinutes(now.getMinutes() + doseInMinutes);
                const hours = now.getHours().toString().padStart(2, '0');
                const minutes = now.getMinutes().toString().padStart(2, '0');
                const timeString = `${hours}:${minutes}`;
                setFormData(prev => ({ ...prev, timeToAdminister: timeString }));
            }
        }
    }, [formData.dose]);

    useEffect(() => {
        // Stop date auto calc
        if (formData.scheduledDatetime && formData.onAnticoagulants) {
            const surgeryDate = formData.scheduledDatetime.slice(0, 10);
            const suggestedStopDate = getNextDay(surgeryDate);

            if (!formData.stopDate) {
                setFormData(prev => ({ ...prev, stopDate: suggestedStopDate }));
            }
        }
    }, [formData.scheduledDatetime, formData.onAnticoagulants]);

    useEffect(() => {
        if (allPatientsData && otBookingsData) {
             // Assuming otBookingsData is an array of bookings, but the API endpoint in original code was get_preop_order_next_ip
             // If it behaves like an array we do this:
             if (Array.isArray(otBookingsData)) {
                const bookedUHIDs = new Set(otBookingsData.map(b => b.uhid_no));
                const availablePatients = allPatientsData.filter(p => !bookedUHIDs.has(p.uhid_no));
                setFilteredPatients(availablePatients);
             } else {
                 setFilteredPatients(allPatientsData);
             }
        } else if (allPatientsData) {
            setFilteredPatients(allPatientsData);
        }
    }, [allPatientsData, otBookingsData]);


    const handleChange = useCallback((event) => {
        const { name, type, checked, value } = event.target;
    
        if (validationErrors[name]) {
          setValidationErrors(prev => ({ ...prev, [name]: '' }));
        }
    
        const autoFillMap = {
          patient_name: {
            list: allPatientsData || [],
            matchField: "Name",
            valueField: "uhid_no",
            idField: "uhid_no"
          },
          primary_surgeon_name: { // Note: not really used in original but kept logic
            list: surgeonList,
            matchField: "name",
            valueField: "id",
            idField: "primary_surgeon_id"
          }
        };
    
        if (name === 'type_of_surgery' || name === 'diagnosis') {
          const selectedSurgery = surgeryData.find(
            surgery => surgery.diagnosisDescription === value
          );
    
          if (selectedSurgery) {
            setFormData(prev => ({
              ...prev,
              diagnosis: value,
              surgical_code: selectedSurgery.icd10Code,
              procedure_code: selectedSurgery.procedureCode,
              procedureDescription: selectedSurgery.procedureDescription,
              proposedSurgery: selectedSurgery.procedureDescription
            }));
          } else {
            setFormData(prev => ({ ...prev, [name]: value }));
          }
        } else if (autoFillMap[name]) {
          const config = autoFillMap[name];
          const selected = config.list.find(item => item[config.matchField] === value);
    
          setFormData(prev => ({
            ...prev,
            [name]: value,
            [config.idField]: selected ? selected[config.valueField] : ""
          }));
        } else {
          setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
          }));
        }
    }, [validationErrors, allPatientsData, setValidationErrors]);

    const handleBlur = useCallback((event) => {
        const { name, value } = event.target;
        validateField(name, value);
    }, [validateField]);

    const handleBloodOrderChange = useCallback((component, value) => {
        const numValue = parseInt(value) || 0;
        if (numValue < 0 || numValue > 20) {
          setValidationErrors(prev => ({
            ...prev,
            [`bloodOrder_${component}`]: 'Units should be between 0-20'
          }));
          return;
        }
    
        setValidationErrors(prev => ({ ...prev, [`bloodOrder_${component}`]: '' }));
    
        setFormData(prev => ({
          ...prev,
          bloodOrders: { ...prev.bloodOrders, [component]: numValue }
        }));
    }, [setValidationErrors]);


    const mutation = useMutation({
        mutationFn: (data) => {
            if (success === "success") {
                return updatePreOpOrder(uhid_no, data);
            } else {
                return savePreOpOrder(data);
            }
        },
        onSuccess: async () => {
             // Send email notification
             setEmailLoading(true);
             try {
                const emailData = {
                  uhid: formData.uhid,
                  patientName: formData.name,
                  surgeonName: formData.surgeonName,
                  diagnosis: formData.diagnosis,
                  proposedSurgery: formData.proposedSurgery,
                  scheduledDatetime: formData.scheduledDatetime,
                  isUpdate: success === "success"
                };
          
                const response = await sendPreOpNotification(emailData);
                setEmailSuccess(response.message);
          
                setTimeout(() => {
                  setEmailSuccess("");
                }, 5000);
          
              } catch (error) {
                console.error('Email sending failed:', error);
                setError('Failed to send email notification to nurses. The order is submitted.');
              } finally {
                setEmailLoading(false);
              }

            showNotification('success', success === 'success' ? 'Pre-operative orders updated successfully!' : 'Pre-operative orders submitted successfully!');
            queryClient.invalidateQueries(['preOpOrder', uhid_no]);
            setSuccess('success');
            clearDraft(DRAFT_KEYS.PREOPERATIVE_ORDERS, uhid_no);
            setTimeout(() => {
                navigate(`/mainPage/${uhid_no}`, {
                  state: { from: 'PreOperativePhase', status: 'success', step: 1 },
                });
            }, 2000);
        },
        onError: (error) => {
             console.error('Submission error:', error);
             const errorMessage = error.response?.data?.error || error.message || 'Unknown error occurred';
             showNotification('error', `Failed to submit pre-operative orders: ${errorMessage}`);
             setError(`Failed to submit pre-operative orders: ${errorMessage}`);
        }
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            setError('Please fix all validation errors before submitting. Check the highlighted fields above.');
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        if (!formData.finalized) {
            setError('Please confirm orders as finalized by checking the checkbox before submitting.');
            const finalizedElement = document.querySelector('[name="finalized"]');
            if (finalizedElement) {
              finalizedElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return;
        }

        setError('');
        const backendData = mapFormToBackend(formData);
        mutation.mutate(backendData);
    };

    return {
        formData,
        setFormData,
        error,
        setError,
        success,
        loading: dataLoading || patientsLoading || mutation.isLoading,
        emailLoading,
        emailSuccess,
        setEmailSuccess,
        validationErrors,
        filteredPatients,
        handleChange,
        handleBlur,
        handleBloodOrderChange,
        handleSubmit,
        mutationLoading: mutation.isLoading
    };
};
